import { inject, Injectable } from "@angular/core";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { PlaidApi } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.api";
import {
  PLAID_PLUGIN_NAME,
  PluginConfigService,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";

/** Thrown after partial Plaid item removal; user-facing toast already shown. */
export class PlaidPartialRemovalError extends Error {
  constructor() {
    super("partial removal");
    this.name = "PlaidPartialRemovalError";
  }
}

/** Thrown after local vault purge fails; user-facing toast already shown. */
export class PlaidDataPurgeFailedError extends Error {
  constructor() {
    super("data purge failed");
    this.name = "PlaidDataPurgeFailedError";
  }
}

@Injectable({
  providedIn: "root",
})
export class PlaidConsentService {
  private plaidApi = inject(PlaidApi);
  private userStore = inject(UserStoreService);
  private transactionStore = inject(TransactionStoreService);
  private pluginConfigService = inject(PluginConfigService);
  private i18nService = inject(I18nService);
  private platformUtilsService = inject(PlatformUtilsService);
  private logService = inject(LogService);

  async removeConsentAndData(): Promise<void> {
    let removedCount = 0;
    let failedCount = 0;

    // Let getItems() failures propagate — abort before touching local data.
    const { items } = await this.plaidApi.getItems();
    for (const item of items) {
      try {
        await this.plaidApi.removeItem(item.item_id);
        removedCount++;
      } catch (error) {
        failedCount++;
        this.logService.error(
          `Failed to remove Plaid item ${item.item_id}: ${JSON.stringify(error)}`,
        );
      }
    }

    if (failedCount > 0) {
      // Do NOT purge local data — backend items still exist, recovery would re-import on next login.
      this.platformUtilsService.showToast(
        "error",
        null,
        this.i18nService.t(
          "plaidItemPartiallyRemoved",
          removedCount.toString(),
          failedCount.toString(),
        ),
      );
      throw new PlaidPartialRemovalError(); // propagate so the caller's catch blocks the success notification
    }

    const plaidAccounts = this.userStore.getPlaidAccounts();
    if (plaidAccounts.length > 0) {
      const isDataDeleted = await this.transactionStore.purgeMultipleAccounts(plaidAccounts);
      if (!isDataDeleted) {
        this.platformUtilsService.showToast(
          "error",
          this.i18nService.t("error"),
          this.i18nService.t("consentRemovedButDataNot"),
        );
        this.logService.error("Plaid items removed but local data could not be purged");
        throw new PlaidDataPurgeFailedError(); // propagate so the caller's catch blocks the success notification
      }
    }

    await this.pluginConfigService.deletePluginByName(PLAID_PLUGIN_NAME);
    // Success notification is owned by the caller (executeUninstallPlaidPlugin).
  }
}
